import type { CenterProps, IntrinsicSystemProps } from '@hover/blueprint';
import { Center, forwardRef } from '@hover/blueprint';
import React from 'react';

import { altStyle } from '../../helpers';

interface ProductSwatchProductProps {
  hexColor?: string;
  imageAltText?: string;
  imageUrl?: string;
}

export interface ProductSwatchProps
  extends CenterProps,
    ProductSwatchProductProps {
  /**
   * Whether to visibly display the `alt` text for material products.
   *
   * @defaultValue false
   *
   * @remarks Material products will always have accessible `alt` text, but
   * because these swatches tend to be so small, it's usually pretty silly.
   */
  displayAltText?: boolean;
  children?: never;
}

const useSwatchProps = ({
  hexColor,
  imageAltText,
  imageUrl,
}: ProductSwatchProductProps) => {
  if (imageUrl) {
    return {
      as: 'img',
      src: imageUrl,
      alt: imageAltText ?? '',
    } satisfies IntrinsicSystemProps<'img'>;
  }

  return {
    bg: hexColor,
  } satisfies CenterProps;
};

/**
 * ProductSwatch is a generic implementation (agnostic of data source). Do not
 * add props that require usage of a specific data source. Create or
 * use a wrapper that destructures the data source into the props needed for this
 * component instead.
 *
 * @see ./ProductVariationProductSwatch.tsx for a wrapper example
 */
export const ProductSwatch = forwardRef<ProductSwatchProps, 'div'>(
  (
    { displayAltText = false, hexColor, imageAltText, imageUrl, sx, ...props },
    ref,
  ) => (
    <Center
      bg="neutral.700"
      borderRadius="300"
      display="block"
      height="500"
      position="relative"
      width="500"
      {...useSwatchProps({ hexColor, imageAltText, imageUrl })}
      sx={{
        ...altStyle('body200', {
          color: 'neutral.500',
          hide: !displayAltText,
        }),
        ...sx,
      }}
      {...props}
      ref={ref}
    />
  ),
);
